import { cached, tracked } from '@glimmer/tracking';
import { getOwner } from '@ember/application';
import { type Registry as Services } from '@ember/service';
import type { PropertyRowDataItem } from 'uplisting-frontend/services/calendar/-internals/types';
import EnquiryModel from 'uplisting-frontend/models/enquiry';
import BookingModel from 'uplisting-frontend/models/booking';
import UnavailabilityModel from 'uplisting-frontend/models/unavailability';
import BookingRequestModel from 'uplisting-frontend/models/booking-request';

export interface IOccasionDto {
  title: string;
  description: string;
  status: string;
}

export class OccasionDto implements IOccasionDto {
  intl!: Services['intl'];

  @tracked _model!: PropertyRowDataItem;

  constructor(context, model: PropertyRowDataItem) {
    this.intl = getOwner(context)?.lookup('service:intl') as Services['intl'];

    this._model = model;
  }

  @cached
  get title(): string {
    const model = this._model;

    if (model instanceof BookingModel) {
      return model.preferredGuestName || model.guestName;
    } else if (
      model instanceof BookingRequestModel ||
      model instanceof EnquiryModel
    ) {
      return model.guestName;
    } else if (model instanceof UnavailabilityModel) {
      return model.description;
    }

    return '';
  }

  @cached
  get description(): string {
    const model = this._model;

    if (
      model instanceof BookingModel ||
      model instanceof BookingRequestModel ||
      model instanceof EnquiryModel
    ) {
      const key = `booking_channel.${model.channel}`;

      if (this.intl.exists(key)) {
        return this.intl.t(key);
      }

      return model.channel;
    } else if (model instanceof UnavailabilityModel) {
      return this.intl.t('calendar.cells.unavailabilities.unavailable');
    }

    return '';
  }

  @cached
  get status(): string {
    const model = this._model;

    if (model instanceof UnavailabilityModel) {
      return model.source || 'unavailable';
    }

    return model.status;
  }

  @cached
  get hasChangeRequestedBy(): boolean {
    const model = this._model;

    if (!(model instanceof BookingModel)) {
      return false;
    }

    return !!model.pendingAlterationRequest;
  }
}
